// Inline dot + text renderer for RAG ratings used in the deliverable
// table's RAG column and the report summary band. Brand spec
// (BRAND_IDENTITY.md §"RAG indicator pattern" + brand SKILL.md) requires dot
// + text label only, never a pill — RAG is a forward-looking risk signal that
// lives next to workflow-status pills on the same row and must NOT borrow the
// chip register or it visually competes with the lifecycle status.
//
// An unset rating renders the neutral "Not assessed" state rather than an
// empty slot: on a surface where a manager declares the rating, a blank must
// read as "nobody has said yet", never as an accidental omission or a green.

import { clsx } from "clsx";
import type { RagRating } from "../../../../types";

type RagIndicatorState = RagRating | "unset";

const DOT_CLASS: Record<RagIndicatorState, string> = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  unset: "bg-slate-400 dark:bg-slate-500",
};

const LABEL: Record<RagIndicatorState, string> = {
  green: "Green",
  amber: "Amber",
  red: "Red",
  unset: "Not assessed",
};

export interface RagIndicatorProps {
  /** `null` renders the neutral "Not assessed" state. */
  rating: RagRating | null;
}

export default function RagIndicator({ rating }: RagIndicatorProps) {
  const state: RagIndicatorState = rating ?? "unset";
  return (
    <span
      data-testid="rag-indicator"
      className="inline-flex items-center gap-1.5 text-body text-slate-700 dark:text-slate-300"
    >
      <span
        className={clsx("h-2 w-2 rounded-full", DOT_CLASS[state])}
        aria-hidden="true"
      />
      <span className="sr-only">RAG status: </span>
      {LABEL[state]}
    </span>
  );
}
